/*
 * Core's routes, code-based (spec 12 §4.1). Two levels: the four full-page authentication screens
 * and a module's full pages (`FullPage`) hang off the root and have no shell and no socket,
 * everything else hangs off one pathless layout route that renders the shell — which is also where
 * a module's routes are mounted, so a module gets the frame, the shortcuts and the announcer without
 * asking for them.
 *
 * The route *pattern* is what may be logged or reported, never the resolved path (spec 12 §5).
 */

import {
	Outlet,
	createRootRoute,
	createRoute,
	redirect,
	useParams,
	useRouter,
	useRouterState,
	type RouteComponent,
} from "@tanstack/react-router"
import { Suspense, lazy, useEffect, useMemo, type ComponentType } from "react"
import type { FullPage, FullPageProps } from "../core/modules/registry.ts"
// Deep import on purpose: core/people/index.ts pulls in the group screens, which import the shell,
// and the shell is what renders this list (core/people/ProfileView.tsx names the same reason).
import { CORE_DETAILS_VIEWS } from "../core/people/ProfileView.tsx"
import { isAdmin } from "../core/people/standing.ts"
import {
	historyStateOf,
	type OpenMemory,
	type SearchLocator,
	type SearchResult,
} from "../core/search/index.ts"
import { Shell, setNavigate, useModel } from "../core/shell/index.ts"
import { conversationTitle, type NameLookup } from "../core/store/conversationName.ts"
import type { Model } from "../core/store/model.ts"
import { HomePage, NotFoundPage } from "./frames.tsx"
import { messageHref, noteRouteShown, rememberRoute } from "./links.ts"

/*
 * The nine screens that are not in the entry chunk.
 *
 * Each is one `import()` of the module that draws it, so its weight arrives when somebody opens that
 * area and never before: a signed-in session downloads none of the signed-out screens, and a reader who
 * never opens Administration never pays for its two tables. The boundary is the **route** and not a
 * component inside one — a `lazy()` reached from the entry makes the bundler split what the entry and
 * the chunk share into a third chunk, and the shell grew by seven kilobytes the one time that was
 * tried (`frames.tsx`) — and the module is named by a literal specifier, which is what lets the
 * bundler see the chunk at all. `ShellLayout` and `AppRoot` hold the two `Suspense` boundaries these
 * suspend at; the area's stylesheet rides its chunk, which is why `scripts/checkBudgets.ts` watches
 * the document's own stylesheet as well as the shell.
 *
 * Home stays in the entry deliberately — it is where `/` lands — and so do the first-run screens it
 * draws: measured, a `lazy()` around those two *cost* the shell 0.8 KB and added a third stylesheet,
 * which is the same answer `frames.tsx` records. What a screen shares with the entry is the measure of
 * whether it can leave it, and the six areas below were each measured on their own (X-B3 D6): every one of
 * them pays between 2.0 and 5.6 KB gzip of the shell, and none of them is on a first screen.
 */
const loadLogin = () => import("../core/auth/pages/LoginPage.tsx")
const loadEnrol = () => import("../core/auth/pages/EnrolPage.tsx")
const loadReset = () => import("../core/auth/pages/ResetPage.tsx")
const LoginScreen = lazy(async () => ({ default: (await loadLogin()).LoginPage }))
const EnrolScreen = lazy(async () => ({ default: (await loadEnrol()).EnrolPage }))
const ResetScreen = lazy(async () => ({ default: (await loadReset()).ResetPage }))

/**
 * The screens that render without a session: the login form and the three that redeem a link out of a
 * mail. Named once, here — `authRoutes` mounts them, the download below starts the one this document
 * was opened at, and `main.tsx` asks `isSignedOutScreen` before it sends a visitor without a session
 * to `/login`, which would throw away the enrolment or reset token the address carries.
 *
 * `/login/code` is the login screen too: the emailed code is typed into the tab that asked for it
 * (spec 01 §4), which is a state of that screen and not a page of its own — the path exists so that a
 * mail client's link lands somewhere sensible rather than on the 404 view.
 */
const SIGNED_OUT_SCREENS: readonly {
	path: string
	load: () => Promise<unknown>
	Screen: RouteComponent
}[] = [
	{ path: "/login", load: loadLogin, Screen: LoginScreen },
	{ path: "/login/code", load: loadLogin, Screen: LoginScreen },
	{ path: "/enrol", load: loadEnrol, Screen: EnrolScreen },
	{ path: "/reset", load: loadReset, Screen: ResetScreen },
]

/** Whether an address is one of the signed-out screens, where a visitor without a session stays. */
export function isSignedOutScreen(pathname: string): boolean {
	return SIGNED_OUT_SCREENS.some((screen) => screen.path === pathname)
}
const SettingsPage = lazy(async () => ({
	default: (await import("../core/settings/SettingsPage.tsx")).SettingsPage,
}))
const PeoplePage = lazy(async () => ({
	default: (await import("../core/people/PeoplePage.tsx")).PeoplePage,
}))
const AdminPage = lazy(async () => ({
	default: (await import("../core/admin/AdminPage.tsx")).AdminPage,
}))
const SearchPage = lazy(async () => ({
	default: (await import("../core/search/SearchPage.tsx")).SearchPage,
}))
const CodeFilePage = lazy(async () => ({
	default: (await import("../core/search/CodeFilePage.tsx")).CodeFilePage,
}))
const ActivityPage = lazy(async () => ({
	default: (await import("../core/activity/ActivityPage.tsx")).ActivityPage,
}))

/**
 * The signed-out screen this document was opened at, asked for while the bundle is still being
 * evaluated.
 *
 * `main.tsx` builds the router and asks for the session together and renders after both, so a screen
 * discovered by the first render is a screen discovered one round trip *late* — and for a visitor
 * without a session that round trip is the whole window. Reading the address here costs the round trip
 * nothing and a reader with a session nothing at all: every path but these four is somebody who must
 * never pay for them. The router resolves the address a moment later; this only starts the download.
 * A module's full page is started the same way, by `main.tsx`, which is where the modules are known.
 */
void SIGNED_OUT_SCREENS.find((screen) => screen.path === location.pathname)?.load()

/**
 * TanStack types its route hooks through the *registered* router type. This application registers
 * none — a module's routes are added at runtime, so that type could never be complete — and the
 * hooks therefore answer `any`. Each screen says instead what shape it expects, in one place.
 */
function useRouteValue<T>(hook: () => unknown): T {
	return hook() as T
}

/**
 * The whole application, and the one thing every screen in it shares: the in-place navigation
 * function of `core/shell/navigate.ts`.
 *
 * It is installed here, and not by a screen, because its callers outlive any one screen. The
 * sidebar is drawn on every route with chrome and the dialogs it opens can be open on any of them,
 * so a holder installed by one route's component is null on the six areas a reader most often
 * opens a conversation from — which is what made that move a full page load (A-03). The sign-in
 * screens hang off the root beside the shell, so the same installation covers their cross-links.
 */
function AppRoot() {
	// TanStack types the router through the *registered* router type, and this application
	// registers none (see useRouteValue above); the one thing needed from it here is a push.
	const router = useRouter() as unknown as { history: { push: (path: string) => void } }
	useEffect(() => {
		setNavigate((path: string) => router.history.push(path))
		return () => setNavigate(null)
	}, [router])
	/*
	 * The outer boundary, for the screens that have no shell around them. It holds nothing while a
	 * signed-out screen's chunk is in flight: the window is a centred panel on an empty ground, and a
	 * spinner or a skeleton of a form is a promise about a shape this visitor has never seen. The
	 * document's own silhouette stands until the first render, and the address the visitor arrived at
	 * has already started the download (`signedOutScreens` above), so in practice there is nothing to
	 * wait for here.
	 */
	return (
		<Suspense fallback={null}>
			<Outlet />
		</Suspense>
	)
}

/**
 * The areas the rail leads to, fetched while the reader is reading.
 *
 * Code splitting moves the weight of a screen off the first paint; it must not move it onto the click
 * that opens the screen. So the four places every signed-in reader can reach — and Administration for
 * the account that has it — are asked for in the background, about fifteen kilobytes, and by the time
 * anybody reaches for one it is in the module cache; `import()` a second time is free.
 *
 * **After the first screen, not with it.** Measured on the production build, an idle callback alone
 * fired 168 ms in, which is before the timeline's own messages were on the wire: five chunks nobody
 * has asked for, competing with the one thing the reader is waiting for. So the grace period comes
 * first and the idle callback after it, and a reader who is typing or still scrolling keeps the
 * machine to themselves for as long as they are busy. Safari has no `requestIdleCallback` and gets
 * the timer alone.
 */
const WARM_AFTER_MS = 2_000

function warmRailAreas(admin: boolean): () => void {
	// A warm-up that fails — the tab went offline, a deploy replaced the chunks — costs nothing: the
	// click that opens the screen imports it again. Left unhandled, its rejection would be an error of
	// the page for something nobody asked for.
	const ignore = () => undefined
	const warm = () => {
		import("../core/activity/ActivityPage.tsx").catch(ignore)
		import("../core/settings/SettingsPage.tsx").catch(ignore)
		import("../core/people/PeoplePage.tsx").catch(ignore)
		import("../core/search/SearchPage.tsx").catch(ignore)
		if (admin) import("../core/admin/AdminPage.tsx").catch(ignore)
	}
	const timer = setTimeout(() => {
		if (typeof requestIdleCallback === "function") requestIdleCallback(warm, { timeout: 4_000 })
		else warm()
	}, WARM_AFTER_MS)
	// The shell outlives every screen, so this only ever runs once — but a test that mounts and
	// unmounts the application must not be left with a timer that imports into a torn-down document.
	return () => clearTimeout(timer)
}

function ShellLayout() {
	const pathname = useRouterState({ select: (state) => state.location.pathname })
	const admin = useModel((model: Model) => isAdmin(model.me))
	useEffect(() => {
		rememberRoute(pathname)
		noteRouteShown(pathname)
	}, [pathname])
	useEffect(() => warmRailAreas(admin), [admin])
	/*
	 * The inner boundary, inside the shell: while an area's chunk is in flight the rail, the sidebar
	 * and the band stay exactly where they are and the page column is empty. That is the honest
	 * picture — the reader is still in the product, one pane of it is still arriving — and it is a
	 * frame or two in practice, because the chunks the rail leads to are fetched while the reader is
	 * reading (`warmRailAreas` below).
	 */
	return (
		<Shell views={CORE_DETAILS_VIEWS}>
			<Suspense fallback={null}>
				<Outlet />
			</Suspense>
		</Shell>
	)
}

/**
 * An address nobody claims. A signed-in reader keeps the frame around it — rail, sidebar, the way
 * to everywhere else — because losing the whole application to a mistyped path is a worse answer
 * than the sentence itself; the page rendered outside the shell before, on an otherwise empty
 * window, and carried the only two landmark violations axe found in the product (X-A3 V11,
 * X-A1 D22). A visitor without a session has no shell to keep: `main.tsx` sends them to `/login`,
 * and until it does the bare page is what they see.
 */
export function NotFoundScreen() {
	const signedIn = useModel((model: Model) => model.me !== null)
	if (!signedIn) return <NotFoundPage />
	return (
		<Shell views={CORE_DETAILS_VIEWS}>
			<NotFoundPage />
		</Shell>
	)
}

export const rootRoute = createRootRoute({
	component: AppRoot,
	notFoundComponent: NotFoundScreen,
})

/**
 * The pathless route that carries the shell; every screen with chrome is a child of it. A
 * `notFound` raised *inside* it — a conversation that is gone — is already framed, so there the
 * page is drawn on its own.
 */
export const shellRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "shell",
	component: ShellLayout,
	notFoundComponent: NotFoundPage,
})

/*
 * Search asks this layer two things about a hit — what the conversation it is in is called, and
 * where the message itself is — because the routes layer is where the application's URL shapes are
 * decided and the client model is where the names live. The search area itself stays free of both.
 *
 * A hit whose conversation is not in the reader's model is not hidden: the row is still shown,
 * without a name and without a link. The server decided it may be read; this layer only fails to
 * address it.
 */
function useSearchLocator(): SearchLocator {
	const conversations = useModel((model: Model) => model.conversations)
	const users = useModel((model: Model) => model.users)
	const me = useModel((model: Model) => model.me)
	return useMemo<SearchLocator>(() => {
		const lookup: NameLookup = { conversations, users, me }
		return {
			name(scopeKind: string, scopeId: number): string | null {
				if (scopeKind !== "conversation") return null
				if (conversations[scopeId] === undefined) return null
				// The naming ladder and not `meta.name`: a direct conversation carries no name on the
				// wire, so every hit inside one was grouped under "A conversation you are no longer
				// in" — a false statement about the reader's own membership (X-A3 V10). Whether the
				// conversation is in the model at all is what that bucket is actually for.
				return conversationTitle(lookup, scopeId)
			},
			href(result: SearchResult): string | null {
				if (result.scopeKind !== "conversation") return null
				const meta = conversations[result.scopeId]?.meta
				return meta === undefined ? null : messageHref(meta, result.scopeSeq)
			},
		}
	}, [conversations, users, me])
}

/**
 * A hit opens in place through the router's history, which carries what the page it opens should
 * hold — a document section's find words — in the entry's memory and never in the address
 * (`core/search/openMemory.ts`); a message's address is a path with its own query and fragment, which
 * the history takes as it is.
 */
function SearchScreen() {
	const router = useRouter() as unknown as {
		history: { push: (path: string, state?: Record<string, string>) => void }
	}
	const locate = useSearchLocator()
	return (
		<SearchPage
			locate={locate}
			onNavigate={(path: string, memory?: OpenMemory) => {
				router.history.push(path, historyStateOf(memory))
			}}
		/>
	)
}

/**
 * The search term is never a route parameter: a query in a URL reaches the browser history, the
 * Referer header and every access log in front of the instance, and a search term is not logged
 * (spec 10, A26). The screen holds it and the route carries nothing.
 */
const searchRoute = createRoute({
	getParentRoute: () => shellRoute,
	path: "/search",
	component: SearchScreen,
})

/**
 * The file a code search hit opens: `/code/<repo>/<path>?line=&at=` (`links.ts`' `codeFileHref`). The
 * path is the splat, so its slashes are the file's own; the line and the commit are query
 * parameters, read as they are and checked by the page and the server, never trusted for more than
 * where to look.
 */
function CodeFileScreen() {
	const { repo, _splat } = useRouteValue<{ repo: string; _splat?: string }>(codeFileRoute.useParams)
	const searchStr = useRouterState({ select: (state) => state.location.searchStr })
	const query = new URLSearchParams(searchStr)
	const line = Number.parseInt(query.get("line") ?? "", 10)
	return (
		<CodeFilePage
			repo={repo}
			path={_splat ?? ""}
			line={Number.isInteger(line) && line >= 1 ? line : null}
			at={query.get("at") ?? ""}
		/>
	)
}

const codeFileRoute = createRoute({
	getParentRoute: () => shellRoute,
	path: "/code/$repo/$",
	component: CodeFileScreen,
})

function SettingsScreen() {
	const { section } = useRouteValue<{ section: string }>(settingsRoute.useParams)
	return <SettingsPage section={section} />
}

const settingsRoute = createRoute({
	getParentRoute: () => shellRoute,
	path: "/settings/$section",
	component: SettingsScreen,
})

function AdminScreen() {
	const { section } = useRouteValue<{ section: string }>(adminRoute.useParams)
	return <AdminPage section={section} />
}

const adminRoute = createRoute({
	getParentRoute: () => shellRoute,
	path: "/admin/$section",
	component: AdminScreen,
})

/**
 * The routes core contributes to the shell. Not every one of them is a rail entry — the rail is
 * places only (UX §1.1) — and two of them are redirects, because a bare section frame is not a page
 * and an address a reader may have bookmarked should keep landing somewhere.
 */
export const coreRoutes = [
	createRoute({ getParentRoute: () => shellRoute, path: "/", component: HomePage }),
	createRoute({ getParentRoute: () => shellRoute, path: "/activity", component: ActivityPage }),
	searchRoute,
	codeFileRoute,
	createRoute({ getParentRoute: () => shellRoute, path: "/people", component: PeoplePage }),
	// A bare /settings or /admin is not a page: it lands on the first section rather than showing
	// a navigation with nothing beside it.
	createRoute({
		getParentRoute: () => shellRoute,
		path: "/settings",
		beforeLoad: () => {
			// redirect() is TanStack's control-flow signal for a route, not an error condition.
			// eslint-disable-next-line @typescript-eslint/only-throw-error
			throw redirect({ to: "/settings/appearance" })
		},
	}),
	settingsRoute,
	createRoute({
		getParentRoute: () => shellRoute,
		path: "/admin",
		beforeLoad: () => {
			// eslint-disable-next-line @typescript-eslint/only-throw-error
			throw redirect({ to: "/admin/people" })
		},
	}),
	adminRoute,
	// Operations is a section of Administration rather than a place of its own (UX §1.1). The old
	// address stays addressable so that a bookmark and a runbook that names it still land.
	createRoute({
		getParentRoute: () => shellRoute,
		path: "/ops",
		beforeLoad: () => {
			// eslint-disable-next-line @typescript-eslint/only-throw-error
			throw redirect({ to: "/admin/operations" })
		},
	}),
]

/**
 * The full-page screens. They are children of the root, not of the shell: signing in happens
 * without a socket, without the model and without a navigation that promises places the visitor
 * cannot reach yet.
 *
 * They are mounted without an `onSignedIn`, so they leave the flow with a full page load
 * (core/auth/flow.ts). That is not a detail of navigation: the client boots once, in main.tsx, and
 * everything a signed-in person needs — the database, the model, the socket, the catch-up — is opened
 * there against the session that existed at boot. A router navigation would hand the shell to somebody
 * whose session was minted a moment ago and whose socket nobody ever dialled, and the connection
 * banner would stay up until they reloaded the page themselves.
 */
export const authRoutes = SIGNED_OUT_SCREENS.map(({ path, Screen }) =>
	createRoute({ getParentRoute: () => rootRoute, path, component: Screen }),
)

/** One full page: its parameters and its raw query string, handed to the module's screen. */
function FullPageFrame({ Screen }: { Screen: ComponentType<FullPageProps> }) {
	// Not strict: the page's route is created at runtime, and TanStack's typed hooks would need the
	// registered router type this application does not have (`useRouteValue` above).
	const params: Record<string, string> = useParams({ strict: false })
	const searchStr = useRouterState({ select: (state) => state.location.searchStr })
	const search = useMemo(() => new URLSearchParams(searchStr), [searchStr])
	return <Screen params={params} search={search} />
}

/**
 * A module's full pages (`FullPage`), mounted beside the sign-in screens and for the same reason: a
 * page drawn without the shell and the client. Each is a `lazy()` of the module's own chunk.
 */
export function fullPageRoutes(pages: readonly FullPage[]) {
	return pages.map((page) => {
		const Screen = lazy(async () => ({ default: await page.load() }))
		return createRoute({
			getParentRoute: () => rootRoute,
			path: page.path,
			component: () => <FullPageFrame Screen={Screen} />,
		})
	})
}
